/**
 * @file 数式つき Markdown を表示用の HTML にする。
 *
 * @what: `$...$` (行中) と `$$...$$` (別行) を KaTeX で組み、残りを marked で HTML にし、
 *   DOMPurify で消毒して返す。数式は marked に通す前に取り出しておき、変換後に戻す。
 * @why: 解説の本文は Claude が書く Markdown で、数式を紙の参考書と同じ見え方で読めることが
 *   要件である (代表判断、2026-09-05)。`_` や `\` を含む数式を marked に通すと強調やエスケープと
 *   誤読されるので、先に退避する。
 * @failure: 数式の構文誤りは KaTeX が赤字で式のまま出す (throwOnError: false)。
 *   marked の失敗は素の文字列のエスケープ表示に落とす (学びメモの renderMarkdown と同じ)。
 */

import DOMPurify from "dompurify";
import katex from "katex";
import { marked } from "marked";

/** 退避した数式の目印で、本文に現れない制御文字で挟む。 */
const MARK_OPEN = "⁣MATH";
const MARK_CLOSE = "⁣";
const MARK_RE = /⁣MATH(\d+)⁣/g;

function escapeHtml(text: string): string {
  return text.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

interface MathChunk {
  tex: string;
  display: boolean;
}

/** 数式を取り出し、本文の同じ位置に目印を置く。 */
function extractMath(text: string): { body: string; chunks: MathChunk[] } {
  const chunks: MathChunk[] = [];
  const put = (tex: string, display: boolean): string => {
    chunks.push({ tex, display });
    return `${MARK_OPEN}${chunks.length - 1}${MARK_CLOSE}`;
  };
  // 別行 ($$...$$) を先に取る。行中 ($...$) の正規表現が $$ を空の数式と読まないようにするため。
  let body = text.replace(/\$\$([\s\S]+?)\$\$/g, (_m, tex: string) => put(tex.trim(), true));
  body = body.replace(/\$([^$\n]+?)\$/g, (_m, tex: string) => put(tex.trim(), false));
  return { body, chunks };
}

function renderTex(chunk: MathChunk): string {
  return katex.renderToString(chunk.tex, {
    displayMode: chunk.display,
    throwOnError: false,
    strict: "ignore",
    output: "htmlAndMathml",
  });
}

export function renderMathMarkdown(text: string): string {
  const { body, chunks } = extractMath(text);
  let html: string;
  try {
    html = marked.parse(body, { async: false, gfm: true, breaks: false });
  } catch (err) {
    console.error("[study] failed to render math markdown", err);
    return `<pre>${escapeHtml(text)}</pre>`;
  }
  const restored = html.replace(MARK_RE, (_m, idx: string) => {
    const chunk = chunks[Number(idx)];
    return chunk ? renderTex(chunk) : "";
  });
  return DOMPurify.sanitize(restored);
}
